import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest, HttpResponse } from '@angular/common/http';
import { PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Observable, catchError, from, of, switchMap, throwError } from 'rxjs';
import { environment } from '@environments/environment';
import { CryptoSessionService } from './crypto-session.service';
import { canInflate, decryptJson, encodeJson, encryptBytes, isEnvelope, randomB64Url, requestAad, responseAad, SessionKeys } from './web-crypto';

/** Mismas exclusiones que el gateway (common/crypto/encryption.middleware.ts): quedan solo con TLS. */
const SKIP: RegExp[] = [/\/crypto\//, /\/api\/docs/, /\/events\/invoice-status/, /\/storage\/(upload|view)/, /\/download-(pdf|xml)/];

const isBinary = (b: unknown): boolean => b instanceof FormData || b instanceof Blob || b instanceof ArrayBuffer || b instanceof URLSearchParams;

async function seal(req: HttpRequest<unknown>, s: SessionKeys): Promise<{ request: HttpRequest<unknown>; nonce: string }> {
  const path = new URL(req.urlWithParams, window.location.origin).pathname;
  const ts = String(Date.now());
  const nonce = randomB64Url(16);
  const aad = requestAad(req.method, path, s.sid, ts, nonce);
  const setHeaders: Record<string, string> = { 'x-enc-sid': s.sid, 'x-enc-ts': ts, 'x-enc-nonce': nonce };
  // El texto cifrado no se comprime: se pide al gateway que comprima el JSON antes de cifrarlo (respuestas hasta ~14 veces más pequeñas).
  // Solo si el gateway lo anunció: así el orden de despliegue (frontend / gateway) no importa.
  if (s.z && canInflate()) setHeaders['x-enc-z'] = '1';
  if (req.body === null || req.body === undefined) {
    // Sin cuerpo: la etiqueta de un mensaje vacío autentica la cabecera.
    const e = await encryptBytes(s.c2s, new Uint8Array(0), aad);
    setHeaders['x-enc-tag'] = `${e.iv}.${e.ct}`;
    return { request: req.clone({ setHeaders }), nonce };
  }
  return { request: req.clone({ setHeaders, body: await encryptBytes(s.c2s, encodeJson(req.body), aad) }), nonce };
}

function send(req: HttpRequest<unknown>, next: HttpHandlerFn, svc: CryptoSessionService, s: SessionKeys, canRetry: boolean): Observable<HttpEvent<unknown>> {
  return from(seal(req, s)).pipe(
    switchMap(({ request, nonce }) =>
      next(request).pipe(
        switchMap((ev) =>
          ev instanceof HttpResponse && isEnvelope(ev.body)
            ? from(decryptJson(s.s2c, ev.body, responseAad(nonce, ev.status)).then((body) => ev.clone({ body })))
            : of(ev),
        ),
        catchError((err: unknown) => {
          if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
          // El gateway reinició o la sesión venció: se abre otra y se reintenta una sola vez.
          if (err.status === 409 && (err.error as { code?: string } | null)?.code === 'ENC_SESSION_INVALID' && canRetry) {
            svc.reset();
            return from(svc.ensure()).pipe(switchMap((ns) => (ns ? send(req, next, svc, ns, false) : throwError(() => err))));
          }
          if (isEnvelope(err.error)) {
            return from(decryptJson(s.s2c, err.error, responseAad(nonce, err.status))).pipe(
              switchMap((error) => throwError(() => new HttpErrorResponse({ error, headers: err.headers, status: err.status, statusText: err.statusText, url: err.url ?? undefined }))),
            );
          }
          return throwError(() => err);
        }),
      ),
    ),
  );
}

/**
 * Cifra el JSON hacia el gateway y descifra sus respuestas (errores incluidos). Va el ÚLTIMO de la cadena: así `errorInterceptor` ya ve los
 * errores en claro y los reintentos del 401 pasan de nuevo por aquí con un nonce nuevo.
 */
export const encryptionInterceptor: HttpInterceptorFn = (req, next) => {
  if (!isPlatformBrowser(inject(PLATFORM_ID))) return next(req);
  if (!req.url.startsWith(environment.apiUrl) || req.responseType !== 'json' || isBinary(req.body)) return next(req);
  const path = new URL(req.urlWithParams, window.location.origin).pathname;
  if (SKIP.some((r) => r.test(path))) return next(req);
  const svc = inject(CryptoSessionService);
  return from(svc.ensure()).pipe(switchMap((s) => (s ? send(req, next, svc, s, true) : next(req))));
};
